import React, { useState, useRef, useEffect } from 'react';
import SimpleTeamPanel from './SimpleTeamPanel';
import Button from '../common/Button';
import {
  DEFAULT_SCORING,
  validateGameScore,
  shouldShowWinQuickButton,
  applyWinQuickScore,
} from '../../utils/gameScoreRules';
import { handleMobileScoreFocus, markSkipMobileScoreClear, mobileScoreInputAttrs } from '../../utils/mobileScoreInput';

/**
 * Simplified court assignment for private sessions
 * Just handles player assignment and score input
 */
const SimpleCourtAssignment = ({
  court,
  courtIndex,
  onUpdateResult,
  onRemoveCourt,
  onRemovePlayerFromCourt,
  playersById,
  availablePlayers = [],
  onAddPlayerToCourt,
  scoringConfig = DEFAULT_SCORING,
}) => {
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');
  const [scoreError, setScoreError] = useState('');

  const team1ScoreInputRef = useRef(null);
  const team2ScoreInputRef = useRef(null);
  const { pointsTarget } = scoringConfig;

  useEffect(() => {
    if (team1Score !== '' && team2Score !== '') {
      const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
      setScoreError(validationError || '');
    } else {
      setScoreError('');
    }
  }, [team1Score, team2Score, scoringConfig]);

  const handleScoreSubmit = () => {
    const score1 = parseInt(team1Score, 10);
    const score2 = parseInt(team2Score, 10);

    if (isNaN(score1) || isNaN(score2)) {
      setScoreError('Please enter valid scores');
      return;
    }

    const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
    if (validationError) {
      setScoreError(validationError);
      return;
    }

    const winningTeam = score1 > score2 ? 1 : 2;

    onUpdateResult(courtIndex, {
      team1Score: score1,
      team2Score: score2,
      winningTeam,
    });

    setTeam1Score('');
    setTeam2Score('');
    setScoreError('');
  };

  const handleScoreChange = (team, value) => {
    if (value === '') {
      if (team === 'team1') {
        setTeam1Score('');
      } else {
        setTeam2Score('');
      }
      return;
    }

    if (/^\d+$/.test(value) && value.length <= 2) {
      if (team === 'team1') {
        setTeam1Score(value);
        if (value.length === 2 && team2ScoreInputRef.current) {
          markSkipMobileScoreClear('team2');
          team2ScoreInputRef.current.focus();
        }
      } else {
        setTeam2Score(value);
        if (value.length === 2 && team1Score.length < 2 && team1ScoreInputRef.current) {
          markSkipMobileScoreClear('team1');
          team1ScoreInputRef.current.focus();
        }
      }
    }
  };

  const handleScoreIncrement = (team, delta) => {
    const currentScore = team === 'team1' ? team1Score : team2Score;
    const currentValue = parseInt(currentScore, 10) || 0;
    const newValue = Math.max(0, Math.min(99, currentValue + delta));

    if (team === 'team1') {
      setTeam1Score(newValue.toString());
    } else {
      setTeam2Score(newValue.toString());
    }
  };

  const showWinQuickButtons = shouldShowWinQuickButton(team1Score, team2Score, pointsTarget);
  const handleWinButton = (team) =>
    applyWinQuickScore(team, pointsTarget, {
      setTeam1Score,
      setTeam2Score,
      team1InputRef: team1ScoreInputRef,
      team2InputRef: team2ScoreInputRef,
      onClearErrors: () => setScoreError(''),
    });

  const isCourtFull = court.team1.length === 2 && court.team2.length === 2;
  const canSubmitScore = isCourtFull && team1Score && team2Score && !scoreError;

  return (
    <div className="bg-surface rounded-lg shadow-brand-sm border border-border-medium p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary">Court {court.courtNumber}</h3>
        <button
          onClick={() => onRemoveCourt(courtIndex)}
          className="text-gray-400 hover:text-red-500 transition-colors"
          title="Remove Court"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <SimpleTeamPanel
          team={court.team1}
          teamNumber={1}
          playersById={playersById}
          canRemove={true}
          onRemovePlayer={onRemovePlayerFromCourt}
          onAddPlayer={onAddPlayerToCourt}
          courtNumber={court.courtNumber}
          availablePlayers={availablePlayers}
        />

        <SimpleTeamPanel
          team={court.team2}
          teamNumber={2}
          playersById={playersById}
          canRemove={true}
          onRemovePlayer={onRemovePlayerFromCourt}
          onAddPlayer={onAddPlayerToCourt}
          courtNumber={court.courtNumber}
          availablePlayers={availablePlayers}
        />
      </div>

      {isCourtFull && (
        <div className="w-full p-2 bg-surface-subtle dark:bg-slate-700 rounded-lg border border-border-medium">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col items-center gap-1.5 flex-1">
              <span className="text-xs font-medium text-team1">Team 1</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleScoreIncrement('team1', -1)}
                  className="hidden sm:flex w-7 h-7 items-center justify-center bg-team1-light hover:bg-team1/30 active:bg-team1/40 text-team1 rounded border border-team1 font-bold text-base transition-colors touch-manipulation"
                  aria-label="Decrease Team 1 score"
                >
                  −
                </button>

                <input
                  ref={team1ScoreInputRef}
                  placeholder="0"
                  value={team1Score}
                  onChange={(e) => handleScoreChange('team1', e.target.value)}
                  onFocus={(e) => handleMobileScoreFocus(e, 'team1', () => setTeam1Score(''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !scoreError && team1Score && team2Score) {
                      handleScoreSubmit();
                    }
                  }}
                  maxLength={2}
                  className="w-16 h-8 px-2 border-2 border-team1 rounded-md bg-surface shadow-sm focus:outline-none focus:ring-2 focus:ring-team1 focus:border-team1 text-center text-lg font-bold text-team1"
                  {...mobileScoreInputAttrs}
                />

                <button
                  type="button"
                  onClick={() => handleScoreIncrement('team1', 1)}
                  className="hidden sm:flex w-7 h-7 items-center justify-center bg-team1-light hover:bg-team1/30 active:bg-team1/40 text-team1 rounded border border-team1 font-bold text-base transition-colors touch-manipulation"
                  aria-label="Increase Team 1 score"
                >
                  +
                </button>
              </div>

              {showWinQuickButtons && (
                <button
                  type="button"
                  onClick={() => handleWinButton('team1')}
                  className="px-4 py-1.5 text-xs font-semibold bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors shadow-sm"
                  aria-label={`Set Team 1 score to ${pointsTarget}`}
                >
                  Win ({pointsTarget})
                </button>
              )}
            </div>

            <div className="flex flex-col items-center gap-0.5">
              <div className="w-6 h-0.5 bg-border-medium"></div>
              <span className="text-sm font-bold text-text-muted">VS</span>
              <div className="w-6 h-0.5 bg-border-medium"></div>
            </div>

            <div className="flex flex-col items-center gap-1.5 flex-1">
              <span className="text-xs font-medium text-team2">Team 2</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleScoreIncrement('team2', -1)}
                  className="hidden sm:flex w-7 h-7 items-center justify-center bg-team2-light hover:bg-team2/30 active:bg-team2/40 text-team2 rounded border border-team2 font-bold text-base transition-colors touch-manipulation"
                  aria-label="Decrease Team 2 score"
                >
                  −
                </button>

                <input
                  ref={team2ScoreInputRef}
                  placeholder="0"
                  value={team2Score}
                  onChange={(e) => handleScoreChange('team2', e.target.value)}
                  onFocus={(e) => handleMobileScoreFocus(e, 'team2', () => setTeam2Score(''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !scoreError && team1Score && team2Score) {
                      handleScoreSubmit();
                    }
                  }}
                  maxLength={2}
                  className="w-16 h-8 px-2 border-2 border-team2 rounded-md bg-surface shadow-sm focus:outline-none focus:ring-2 focus:ring-team2 focus:border-team2 text-center text-lg font-bold text-team2"
                  {...mobileScoreInputAttrs}
                />

                <button
                  type="button"
                  onClick={() => handleScoreIncrement('team2', 1)}
                  className="hidden sm:flex w-7 h-7 items-center justify-center bg-team2-light hover:bg-team2/30 active:bg-team2/40 text-team2 rounded border border-team2 font-bold text-base transition-colors touch-manipulation"
                  aria-label="Increase Team 2 score"
                >
                  +
                </button>
              </div>

              {showWinQuickButtons && (
                <button
                  type="button"
                  onClick={() => handleWinButton('team2')}
                  className="px-4 py-1.5 text-xs font-semibold bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                  aria-label={`Set Team 2 score to ${pointsTarget}`}
                >
                  Win ({pointsTarget})
                </button>
              )}
            </div>
          </div>

          {scoreError && (
            <div className="text-danger-dark text-sm mt-2 text-center">
              {scoreError}
            </div>
          )}

          <div className="mt-4 text-center">
            <Button
              onClick={handleScoreSubmit}
              disabled={!canSubmitScore}
              variant="primary"
              size="small"
              className="px-6 py-2 rounded-md font-medium"
            >
              Submit Score
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleCourtAssignment;
